import React from "react";
import { Box, Divider, Typography, useMediaQuery } from "@mui/material";
import { Images } from "../../../utils/Images";
import { formatCompactInr } from "../../../utils/formatCompactInr";

const sanitizeUrl = (url) => String(url || "").replace(/%22$/i, "").replace(/"+$/, "");
const toFlag = (value) => Number(value) === 1;

const MediaProgressSection = ({ vendor }) => {
  const isMobile = useMediaQuery("(max-width: 599.95px)");
  const isTablet = useMediaQuery("(min-width:600px) and (max-width:899.95px)");
  const isLaptop = useMediaQuery("(min-width:900px) and (max-width:1199.95px)");

  const goalAmount = Number(vendor?.goal_amount || 0);
  const raisedAmount = Number(vendor?.raised_amount || 0);
  const goal = formatCompactInr(goalAmount);
  const raised = formatCompactInr(raisedAmount);
  const supporters = String(vendor?.supporters ?? vendor?.supporters_count ?? 0);
  const hideGoal = toFlag(vendor?.hide_goal);
  const hideRaised = toFlag(vendor?.hide_raised);
  const hideAll = toFlag(vendor?.hide_all);
  // Backend sends progress_percentage computed before hide_* flags null out goal/raised amounts,
  // so the bar stays accurate even when those figures aren't in the payload. Fall back to a local
  // calc only if the field is missing (e.g. older cached response).
  const progress =
    vendor?.progress_percentage != null
      ? Number(vendor.progress_percentage)
      : goalAmount > 0
        ? Math.min(Math.round((raisedAmount / goalAmount) * 100), 100)
        : 0;

  const heroMedia = sanitizeUrl(vendor?.banner_image) || Images.fallBackImageBanner;
  const isVideo = /\.(mp4|webm|ogg|mov)(\?|$)/i.test(heroMedia);

  return (
    <Box
      sx={{
        width: isMobile || isTablet ? "100%" : "94.44%",
        maxWidth: "1440px",
        mx: "auto",
        px: isMobile || isTablet ? "16px" : 0,
        mt: isMobile ? "0px" : isTablet ? "20px" : "24px",
        mb: isMobile ? "20px" : "50px",
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: isMobile ? "300px" : isTablet ? "380px" : isLaptop ? "460px" : "535px",
          ...(isMobile && {
            aspectRatio: "2.7/1",
            maxHeight: "250px",
            "@supports (aspect-ratio: 2.7/1)": { height: "auto" },
          }),
          borderRadius: isMobile ? "12px" : isTablet ? "28px" : "36px",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {isVideo ? (
          <Box component="video" src={heroMedia} autoPlay muted loop playsInline sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <Box component="img" src={heroMedia} alt="Vendor media" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
        )}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: isMobile
              ? "linear-gradient(94.546deg, rgba(20,20,20,0.6) 7.86%, rgba(20,20,20,0) 60.821%)"
              : "linear-gradient(100.086deg, rgba(20,20,20,0.6) 21.976%, rgba(20,20,20,0) 60.714%)",
          }}
        />
      </Box>

      <Box
        sx={{
          width: isMobile ? "94.44%" : isTablet ? "100%" : "92.65%",
          mx: "auto",
          mt: isMobile ? "-44px" : isTablet ? "-44px" : isLaptop ? "-72px" : "-98px",
          position: "relative",
          zIndex: 2,
          borderRadius: isMobile ? "8px" : isTablet ? "22px" : "20px",
          background: "#FFFFFF",
          boxShadow: isMobile
            ? "0px 1.687px 1.687px 0px rgba(0,0,0,0.09)"
            : "0px 7px 7px 0px rgba(0,0,0,0.09)",
          px: isMobile ? "10px" : isTablet ? "20px" : "30px",
          py: isMobile ? "10px" : isTablet ? "20px" : "24px",
        }}
      >
        {/* Progress bar always shown, even when "Hide All" is on — only the goal/raised/supporters row below is gated by hideAll */}
        <Box sx={{ position: "relative", height: isMobile ? "16px" : isTablet ? "24px" : "28px", mb: isMobile ? "16px" : isTablet ? "20px" : "24px", width: isMobile ? "94.44%" : "100%", maxWidth: "100%", mx: "auto" }}>
          <Box sx={{ position: "absolute", left: 0, right: 0, top: isMobile ? "2.88px" : isTablet ? "6px" : "4px", height: isMobile ? "8.633px" : isTablet ? "12px" : "16px", borderRadius: "20.438px", background: "#E8E8E8", opacity: 0.6 }} />
          <Box sx={{ position: "absolute", left: isMobile ? "2.16px" : "0.34%", top: isMobile ? "5.04px" : isTablet ? "8px" : "8px", width: `${progress}%`, height: isMobile ? "4.317px" : isTablet ? "8px" : "8px", borderRadius: isMobile ? "2.878px" : "4.088px", background: "linear-gradient(90deg, #9CBA4D 0%, #9AB453 100%)" }} />
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: progress <= 8 ? "0%" : progress >= 95 ? "calc(100% - 60px)" : `calc(${progress}% - 28px)`,
              height: isMobile ? "15.827px" : isTablet ? "24px" : "28px",
              px: isMobile ? "5.755px" : "8px",
              borderRadius: isMobile ? "2.878px" : "4.088px",
              background: "#8EA946",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: isMobile ? "10.072px" : isTablet ? "13px" : "14px", lineHeight: isMobile ? "12.95px" : "18.394px", color: "#FFFFFF", letterSpacing: isMobile ? "-0.2014px" : "normal" }}>
              {progress}%
            </Typography>
          </Box>
        </Box>

        {!hideAll && (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexDirection: "row", gap: isMobile ? "10px" : isTablet ? "10px" : 0, width: isMobile ? "100%" : "auto" }}>
            {!hideGoal && !hideRaised && (
              <>
                {[
                  { key: "Goal", value: goal, icon: Images.rupee },
                  { key: "Raised", value: raised, icon: Images.rupee },
                ].map((item) => (
                  <React.Fragment key={item.key}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? "6px" : isTablet ? "10px" : "14px", justifyContent: "center", flex: isMobile ? 1 : "unset", minWidth: 0 }}>
                      <Box sx={{ width: isMobile ? "24px" : isTablet ? "40px" : "50px", height: isMobile ? "24px" : isTablet ? "40px" : "50px", borderRadius: isMobile ? "5.755px" : "11.111px", border: isMobile ? "0.192px solid #D1DFAD" : "0.37px solid #D1DFAD", background: "#F5F8ED", display: "grid", placeItems: "center" }}>
                        <Box component="img" src={item.icon} alt={item.key} sx={{ width: isMobile ? "14px" : isTablet ? "20px" : "26.667px", height: isMobile ? "14px" : isTablet ? "20px" : "26.667px" }} />
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: isMobile ? "1.439px" : "4px", minWidth: 0 }}>
                        <Typography sx={{ fontWeight: item.key === "Raised" ? 700 : 500, fontSize: isMobile ? "10px" : isTablet ? "14px" : "16px", lineHeight: isMobile ? "12.95px" : "18.394px", color: "#686866", textTransform: "capitalize", whiteSpace: "nowrap" }}>
                          {item.key}
                        </Typography>
                        <Typography sx={{ fontWeight: item.key === "Raised" ? 800 : 700, fontSize: isMobile ? "12px" : isTablet ? "14px" : "16px", lineHeight: isMobile ? "12.95px" : "18.394px", color: "#1D1D1B", whiteSpace: "nowrap", letterSpacing: isMobile ? "-0.24px" : "normal" }}>
                          {item.value}
                        </Typography>
                      </Box>
                    </Box>
                    <Divider orientation="vertical" flexItem sx={{ borderColor: "#B9B9B8", opacity: 0.6, mx: isMobile ? "2px" : 0, height: isMobile ? "23.022px" : "auto", alignSelf: "center" }} />
                  </React.Fragment>
                ))}
              </>
            )}

            {hideGoal && !hideRaised && (
              <>
                <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? "6px" : isTablet ? "10px" : "14px", justifyContent: "center", flex: isMobile ? 1 : "unset", minWidth: 0 }}>
                  <Box sx={{ width: isMobile ? "24px" : isTablet ? "40px" : "50px", height: isMobile ? "24px" : isTablet ? "40px" : "50px", borderRadius: isMobile ? "5.755px" : "11.111px", border: isMobile ? "0.192px solid #D1DFAD" : "0.37px solid #D1DFAD", background: "#F5F8ED", display: "grid", placeItems: "center" }}>
                    <Box component="img" src={Images.rupee} alt="Raised" sx={{ width: isMobile ? "14px" : isTablet ? "20px" : "26.667px", height: isMobile ? "14px" : isTablet ? "20px" : "26.667px" }} />
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: isMobile ? "1.439px" : "4px", minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 500, fontSize: isMobile ? "10px" : isTablet ? "14px" : "16px", lineHeight: isMobile ? "12.95px" : "18.394px", color: "#686866", textTransform: "capitalize", whiteSpace: "nowrap" }}>
                      Raised
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: isMobile ? "12px" : isTablet ? "14px" : "16px", lineHeight: isMobile ? "12.95px" : "18.394px", color: "#1D1D1B", whiteSpace: "nowrap", letterSpacing: isMobile ? "-0.24px" : "normal" }}>
                      {raised}
                    </Typography>
                  </Box>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ borderColor: "#B9B9B8", opacity: 0.6, mx: isMobile ? "2px" : 0, height: isMobile ? "23.022px" : "auto", alignSelf: "center" }} />
                <Box sx={{ flex: 1, minWidth: 0, px: "4px" }}>
                  <Typography sx={{ fontWeight: 500, fontSize: isMobile ? "10px" : "14px", lineHeight: isMobile ? "12.95px" : "18.394px", color: "#686866", textAlign: "center" }}>
                    Support for the profiled vendor and community in need
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ borderColor: "#B9B9B8", opacity: 0.6, mx: isMobile ? "2px" : 0, height: isMobile ? "23.022px" : "auto", alignSelf: "center" }} />
              </>
            )}

            {hideGoal && hideRaised && (
              <>
                <Box sx={{ flex: 1, minWidth: 0, px: "4px" }}>
                  <Typography sx={{ fontWeight: 500, fontSize: isMobile ? "10px" : "14px", lineHeight: isMobile ? "12.95px" : "18.394px", color: "#686866", textAlign: "left" }}>
                    Support for the profiled vendor and community in need
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ borderColor: "#B9B9B8", opacity: 0.6, mx: isMobile ? "2px" : 0, height: isMobile ? "23.022px" : "auto", alignSelf: "center" }} />
              </>
            )}

            <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? "6px" : isTablet ? "10px" : "14px", justifyContent: "center", flex: isMobile ? 1 : "unset", minWidth: 0 }}>
              <Box sx={{ width: isMobile ? "24px" : isTablet ? "40px" : "50px", height: isMobile ? "24px" : isTablet ? "40px" : "50px", borderRadius: isMobile ? "5.755px" : "11.111px", border: isMobile ? "0.192px solid #D1DFAD" : "0.37px solid #D1DFAD", background: "#F5F8ED", display: "grid", placeItems: "center" }}>
                <Box component="img" src={Images.people} alt="Supporters" sx={{ width: isMobile ? "14px" : isTablet ? "20px" : "26px", height: isMobile ? "14px" : isTablet ? "20px" : "26px" }} />
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: isMobile ? "1.439px" : "8px", minWidth: 0 }}>
                <Typography sx={{ fontWeight: 500, fontSize: isMobile ? "10px" : isTablet ? "14px" : "16px", lineHeight: isMobile ? "12.95px" : "18.394px", color: "#686866", whiteSpace: "nowrap" }}>
                  Supporters
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: isMobile ? "12px" : isTablet ? "14px" : "16px", lineHeight: isMobile ? "12.95px" : "18.394px", color: "#1D1D1B", whiteSpace: "nowrap", letterSpacing: isMobile ? "-0.24px" : "normal" }}>
                  {supporters}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MediaProgressSection;
